/**
 * For custom Prompt objects we don't know how to show a chat view,
 * so we just show our generic object values viewer.
 */
import Box from '@mui/material/Box';
import React, {useMemo} from 'react';

import {WeaveCHTableSourceRefContext} from '../pages/CallPage/DataTableView';
import {ObjectViewerSection} from '../pages/CallPage/ObjectViewerSection';
import {CenteredAnimatedLoader} from '../pages/common/Loader';
import {ScrollableTabContent} from '../pages/common/SimplePageLayout';
import {useWFHooks} from '../pages/wfReactInterface/context';
import {CustomWeaveTypeProjectContext} from '../typeViews/CustomWeaveTypeDispatcher';

type CustomPromptValuesTabProps = {
  entityName: string;
  projectName: string;
  refUri: string;
};

export const CustomPromptValuesTab = ({
  entityName,
  projectName,
  refUri,
}: CustomPromptValuesTabProps) => {
  const {useRefsData} = useWFHooks();

  const data = useRefsData({refUris: [refUri]});
  const viewerData = useMemo(() => {
    if (data.loading) {
      return {};
    }
    return data.result?.[0] ?? {};
  }, [data.loading, data.result]);

  const viewerDataAsObject = useMemo(() => {
    const dataIsPrimitive =
      typeof viewerData !== 'object' ||
      viewerData === null ||
      Array.isArray(viewerData);
    if (dataIsPrimitive) {
      // _result is a special key that is automatically removed by the
      // ObjectViewerSection component.
      return {_result: viewerData};
    }
    return viewerData;
  }, [viewerData]);

  return (
    <ScrollableTabContent>
      <Box
        sx={{
          flex: '0 0 auto',
          height: '100%',
        }}>
        {data.loading ? (
          <CenteredAnimatedLoader />
        ) : (
          <WeaveCHTableSourceRefContext.Provider value={refUri}>
            <CustomWeaveTypeProjectContext.Provider
              value={{entity: entityName, project: projectName}}>
              <ObjectViewerSection
                title=""
                data={viewerDataAsObject}
                isExpanded
              />
            </CustomWeaveTypeProjectContext.Provider>
          </WeaveCHTableSourceRefContext.Provider>
        )}
      </Box>
    </ScrollableTabContent>
  );
};
